import { axiosNotifications } from "../api/axios";
import { NOTIFICATIONS_PATHS } from "../api/endpoints";

function decodeVapidKey(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = `${value}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
}

export async function enablePushNotifications(userId) {
  if (!userId) throw new Error("Sign in before enabling browser notifications.");
  if (!window.isSecureContext) {
    const error = new Error(
      "Browser notifications require HTTPS. Open the secure staging URL and try again."
    );
    console.warn("[NOTIF-PUSH] Insecure context; push setup is unavailable.");
    throw error;
  }
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    throw new Error("Browser push notifications are not supported.");
  }

  if (Notification.permission === "denied") {
    throw new Error("Browser notifications are blocked in your browser settings.");
  }

  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was not granted.");
  }

  const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (!vapidPublicKey) {
    throw new Error("Browser notifications are not configured on this deployment.");
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ||
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: decodeVapidKey(vapidPublicKey),
    }));

  const ownerStorageKey = "notificationPushOwner";
  const previousOwner = localStorage.getItem(ownerStorageKey);
  if (previousOwner && previousOwner !== String(userId)) {
    await axiosNotifications.delete(
      NOTIFICATIONS_PATHS.PUSH_SUBSCRIPTIONS(previousOwner),
      { data: { endpoint: subscription.endpoint }, withCredentials: true }
    );
  }

  await axiosNotifications.post(
    NOTIFICATIONS_PATHS.PUSH_SUBSCRIPTIONS(userId),
    subscription.toJSON(),
    { withCredentials: true }
  );
  localStorage.setItem(ownerStorageKey, String(userId));
  return subscription;
}

export async function disablePushNotifications(userId) {
  if (!("serviceWorker" in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) {
    localStorage.removeItem("notificationPushOwner");
    return;
  }

  const owner = localStorage.getItem("notificationPushOwner") || userId;
  if (owner) {
    await axiosNotifications.delete(
      NOTIFICATIONS_PATHS.PUSH_SUBSCRIPTIONS(owner),
      { data: { endpoint: subscription.endpoint }, withCredentials: true }
    );
  }
  await subscription.unsubscribe();
  localStorage.removeItem("notificationPushOwner");
}
